import React from 'react';
import { Award, Mic, Briefcase, FileCheck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { awards as awardsList, workshops as workshopsList, invitedTalks, journalReviewer } from '../data/cvData';

const Awards = () => {
  const awards = awardsList;
  const workshops = workshopsList;

  const patents = [
    {
      title: 'A bipolar magnetic connector for establishing electric contacts between two modules',
      inventors: 'Chetan Pakhare, Tapas Pandey, Nelson Muthu, B. Ravi, Alaric Aroojis',
      appNo: '201721027799',
      date: '04/08/2017',
      status: 'Granted',
      patentNo: '455431',
      grantDate: '27-09-2023',
    },
    {
      title: 'A data logger which can interface several resistive transducers',
      inventors: 'Tapas Pandey, Chetan Pakhare, Nelson Muthu, B. Ravi, Alaric Aroojis',
      appNo: '201721027804',
      date: '04/08/2017',
      status: 'Filed',
    },
    {
      title: 'A Portable Mechanical Ventilator for Respiratory Emergencies',
      inventors: 'Sanchit Jhunjhunwala, Sajan Kapil, Joseph Tharion, Nelson Muthu',
      appNo: '202031045726',
      date: '20-10-2020',
      status: 'Granted',
      patentNo: '546924',
      grantDate: '01-08-2024',
    },
    {
      title: 'A Modular Reciprocating Gait Orthosis For Rehabilitation And Walking Aid For Paraplegic patients',
      inventors: 'Sasibhushan Yengala, Nikhil Kumar Singh, M. Muthupalaniappan, Nelson Muthu, Subramani Kanagaraj',
      appNo: '202131010431',
      date: '12-03-2021',
      status: 'Filed',
    },
    {
      title: 'A Versatile Standing Cum Sitting Device For Rehabilitation And Standing Aid For Paraplegic Patients',
      inventors: 'Yengala Sasibhushan, Nelson Muthu, Subramani Kanagaraj',
      appNo: '202131028647',
      date: '25-06-2021',
      status: 'Filed',
    },
    {
      title: 'Preparation Method for Magnesium Titanate-Integrated Hybrid Polymer-Ceramic Scaffold',
      inventors: 'P Vicky Kumar, CH Nagesh, K Kanchana, Anil Kumar Birru, Nelson Muthu',
      appNo: '202431033972',
      date: '10-10-2024',
      status: 'Filed',
    },
    {
      title: 'Negative Pressure Irrigation System for Endodontic Treatment',
      inventors: 'Vicky Kumar Papulappa, Anil Kumar Birru, Nelson Muthu, Aman Deep Kaur',
      appNo: '202024105588',
      date: '18-11-2024',
      status: 'Filed',
    },
    {
      title: 'Player Friendly Mouthguard Design',
      inventors: 'Vicky Kumar Papulappa, Anil Kumar Birru, Nelson Muthu, Aman Deep Kaur',
      appNo: '202024105586',
      date: '18-11-2024',
      status: 'Filed',
    },
  ];

  return (
    <section id="awards" className="section-container bg-white">
      <h2 className="section-title">Awards & Recognition</h2>
      
      <div className="w-full">
        <Tabs defaultValue="awards" className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8">
            <TabsTrigger value="awards">Awards</TabsTrigger>
            <TabsTrigger value="patents">Patents</TabsTrigger>
            <TabsTrigger value="talks">Invited Talks</TabsTrigger>
            <TabsTrigger value="reviewer">Reviewer</TabsTrigger>
          </TabsList>

          <TabsContent value="awards">
            <div className="grid gap-6">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Award size={32} className="text-[#1e3a8a]" />
                    <h3 className="text-2xl font-bold text-[#1e3a8a]">Honors & Awards</h3>
                  </div>
                  <div className="space-y-4">
                    {awards.map((award, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <Award size={20} className="text-[#1e3a8a] mt-1 flex-shrink-0" />
                        <p className="text-[#475569]">{award}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Briefcase size={32} className="text-[#1e3a8a]" />
                    <h3 className="text-2xl font-bold text-[#1e3a8a]">Workshops & Conferences Organized</h3>
                  </div>
                  <div className="space-y-3">
                    {workshops.map((workshop, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                        <span className="text-[#1e3a8a] font-bold">•</span>
                        <p className="text-[#475569]">{workshop}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="patents">
            <div className="space-y-4">
              <Accordion type="single" collapsible className="w-full">
                {patents.map((patent, index) => (
                  <AccordionItem key={index} value={`patent-${index}`}>
                    <AccordionTrigger className="text-left hover:text-[#1e3a8a]">
                      <div className="flex items-start gap-3">
                        <FileCheck size={20} className="text-[#1e3a8a] mt-1 flex-shrink-0" />
                        <span className="font-semibold">{patent.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-8 space-y-2 text-[#64748b]">
                        <p><span className="font-semibold">Inventors:</span> {patent.inventors}</p>
                        <p><span className="font-semibold">Application No:</span> {patent.appNo}</p>
                        <p><span className="font-semibold">Date of Application:</span> {patent.date}</p>
                        {patent.status === 'Granted' && (
                          <>
                            <p className="text-green-600 font-bold"><span className="font-semibold">Status:</span> Granted</p>
                            <p className="text-green-600"><span className="font-semibold">Patent No:</span> {patent.patentNo}</p>
                            <p className="text-green-600"><span className="font-semibold">Grant Date:</span> {patent.grantDate}</p>
                          </>
                        )}
                        {patent.status === 'Filed' && (
                          <p className="text-blue-600 font-semibold"><span className="font-semibold">Status:</span> Filed</p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </TabsContent>

          <TabsContent value="talks">
            <div className="grid gap-4">
              {invitedTalks.map((talk, index) => (
                <Card key={index} className="hover-lift">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <Mic size={24} className="text-[#1e3a8a]" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-[#1e3a8a] mb-2">
                          {talk.title}
                        </h4>
                        <p className="text-[#64748b] mb-1">{talk.venue}</p>
                        <p className="text-sm text-[#64748b]">{talk.year}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviewer">
            <Card className="border-2 border-slate-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">Journal Reviewer</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {journalReviewer.map((journal, index) => (
                    <div key={index} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                      <FileCheck size={18} className="text-[#1e3a8a] mt-1 flex-shrink-0" />
                      <p className="text-[#475569]">{journal}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Awards;